import { datadogRum } from '@datadog/browser-rum';
import config from './config';

const DATADOG_USER_STORAGE_KEY = 'datadog-live-user-context';
const COGNITO_AUTH_KEY_PREFIX = 'CognitoIdentityServiceProvider';

interface DatadogUserIdentity {
  userId: string;
  email: string;
  organisationId: string;
  role: number;
  isStaff: boolean;
}

interface DatadogTrackedUser extends Partial<DatadogUserIdentity> {
  [key: string]: string | number | boolean | null | undefined;
  lastAuthUser?: string | null;
  loggedOut?: boolean;
  logoutMethod?: string | null;
  sessionIssue?: string | null;
  appVersion?: string | null;
  dualChannelAppVersion?: string | null;
}

function canUseLocalStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function saveTrackedUser(user: DatadogTrackedUser) {
  if (!canUseLocalStorage()) {
    return;
  }

  window.localStorage.setItem(DATADOG_USER_STORAGE_KEY, JSON.stringify(user));
}

function getTrackedUser(): DatadogTrackedUser | null {
  if (!canUseLocalStorage()) {
    return null;
  }

  const rawUser = window.localStorage.getItem(DATADOG_USER_STORAGE_KEY);
  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser) as DatadogTrackedUser;
  } catch (error) {
    console.error('Unable to parse stored Datadog user context', error);
    window.localStorage.removeItem(DATADOG_USER_STORAGE_KEY);
    return null;
  }
}

function getCookieValue(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${encodeURIComponent(name)}=`) || item.startsWith(`${name}=`));

  if (!cookie) {
    return null;
  }

  const value = cookie.slice(cookie.indexOf('=') + 1);

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function withCurrentVersions(user: DatadogTrackedUser, dualChannelAppVersion?: string | null): DatadogTrackedUser {
  return {
    ...user,
    appVersion: config.AppVersion,
    ...(dualChannelAppVersion != null && { dualChannelAppVersion }),
  };
}

function getCognitoLastAuthUser(): string | null {
  const key = `${COGNITO_AUTH_KEY_PREFIX}.${config.Cognito.ClientId}.LastAuthUser`;
  return getCookieValue(key);
}

function withCognitoLastAuthUser(user: DatadogTrackedUser): DatadogTrackedUser {
  const lastAuthUser = getCognitoLastAuthUser();
  if (!lastAuthUser && !user.lastAuthUser) {
    return user;
  }

  return {
    ...user,
    lastAuthUser: lastAuthUser ?? user.lastAuthUser,
  };
}

export async function hydrateDatadogUserContext() {
  const trackedUser = getTrackedUser();
  const lastAuthUser = getCognitoLastAuthUser();
  if (!trackedUser && !lastAuthUser) {
    return;
  }

  const baseUser = withCurrentVersions(withCognitoLastAuthUser(trackedUser ?? {}));
  datadogRum.setUser(baseUser);

  if (window.utilsAPI) {
    const dualChannelAppVersion = await window.utilsAPI.getVersion();
    datadogRum.setUser(withCurrentVersions(withCognitoLastAuthUser(trackedUser ?? {}), dualChannelAppVersion));
  }
}

export function trackDatadogLogin(user: DatadogUserIdentity) {
  const trackedUser: DatadogTrackedUser = withCognitoLastAuthUser({
    ...user,
    loggedOut: false,
    logoutMethod: null,
    sessionIssue: null,
  });

  saveTrackedUser(trackedUser);
  datadogRum.setUser(withCurrentVersions(trackedUser));
}

export function trackDatadogLogout(logoutMethod: string) {
  const trackedUser = getTrackedUser();
  if (!trackedUser) {
    return;
  }

  const loggedOutUser: DatadogTrackedUser = withCognitoLastAuthUser({
    ...trackedUser,
    loggedOut: true,
    logoutMethod,
  });

  saveTrackedUser(loggedOutUser);
  datadogRum.setUser(withCurrentVersions(loggedOutUser));
}

export function trackDatadogSessionIssue(sessionIssue: string) {
  const trackedUser = getTrackedUser();
  if (!trackedUser) {
    return;
  }

  const updatedUser: DatadogTrackedUser = withCognitoLastAuthUser({
    ...trackedUser,
    sessionIssue,
  });

  saveTrackedUser(updatedUser);
  datadogRum.setUser(withCurrentVersions(updatedUser));
}
